import { useEffect } from 'react';
import { Alert, Checkbox, Form, Input, Modal, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { App as AntApp } from 'antd';

import { ApiError } from '@starling/core/api/errors';
import { ErrorAlert } from '@/components/ErrorAlert';
import {
  categoryLabel,
  eligibleOrganizations,
  hasContacts,
  useCategoriesQuery,
  useCreateListingMutation,
  useMyOrganizationsQuery,
  type ListingFeed,
  type MyOrganization,
} from './hooks';

interface PublishValues {
  organization_id?: string;
  feed: ListingFeed;
  category_id: string;
  title: string;
  description: string;
  city: string;
  contact_phone: string;
  /** «Телефон есть в WhatsApp» (№22) — всегда false без телефона. */
  contact_whatsapp?: boolean;
  contact_telegram: string;
}

/** Город и контакты организации → дефолты формы (доводка V1 №16, №22). */
function defaultsFrom(org: MyOrganization | undefined): Partial<PublishValues> {
  return {
    city: org?.city ?? '',
    contact_phone: org?.phone ?? '',
    contact_whatsapp: org?.whatsapp ?? false,
    contact_telegram: org?.telegram ?? '',
  };
}

const FEEDS: ListingFeed[] = ['demand', 'offer', 'notice'];

// Модалка «Разместить объявление» (V1.5): публикация в ленты Vision от имени
// организации текущего аккаунта. Гейт (организация active + verified)
// проверяет бэкенд; UI отсекает заведомо закрытое состояние подводкой —
// подтвердить организацию можно только в кабинете Starling Vision, здесь
// формы верификации нет намеренно (бэкенд-границы V1.5 не трогаем).
export function PublishListingModal({
  open,
  defaultFeed,
  onClose,
}: {
  open: boolean;
  /** Активная вкладка раздела — предзаполняет поле «Лента». */
  defaultFeed: ListingFeed;
  onClose: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<PublishValues>();

  const orgsQuery = useMyOrganizationsQuery({ enabled: open });
  const categories = useCategoriesQuery().data?.items;
  const createMutation = useCreateListingMutation();

  const verified = eligibleOrganizations(orgsQuery.data?.items);
  // Организации догружаются после открытия модалки, а initialValues AntD
  // применяются лишь при монтировании — подставляем город/контакты первой
  // подходящей организации эффектом по приходу данных (№16).
  const orgItems = orgsQuery.data?.items;
  useEffect(() => {
    if (!open || !orgItems) return;
    const first = eligibleOrganizations(orgItems).filter(hasContacts)[0];
    if (first) form.setFieldsValue(defaultsFrom(first));
  }, [open, orgItems, form]);
  // Публиковать может verified-организация С контактами (№16): телефон оптики
  // из «Настроек» зеркалится в организацию, Telegram — в кабинете Vision.
  const eligible = verified.filter(hasContacts);
  const gateClosed = orgsQuery.isSuccess && eligible.length === 0;
  const gateKind: 'not_verified' | 'no_contacts' =
    verified.length > 0 && eligible.length === 0 ? 'no_contacts' : 'not_verified';

  // 403 organization_not_verified / organization_disabled / organization_no_contacts —
  // бэкенд-гейт сработал позже нашей проверки (например, организацию отключили) —
  // показываем подводку вместо сырой ошибки.
  const forbidden = createMutation.error instanceof ApiError && createMutation.error.status === 403;
  const forbiddenNoContacts =
    createMutation.error instanceof ApiError &&
    createMutation.error.code === 'organization_no_contacts';
  // 422 whatsapp_requires_phone (№22) — UI сам снимает флаг без телефона,
  // но на случай гонки показываем осмысленный текст вместо кода ошибки.
  const whatsappRequiresPhone =
    createMutation.error instanceof ApiError &&
    createMutation.error.code === 'whatsapp_requires_phone';

  const handleOk = (): void => {
    // Отклонение validateFields = ошибки валидации, AntD уже показал их под
    // полями; без catch это unhandled rejection (в vitest — красный прогон).
    void form
      .validateFields()
      .catch(() => null)
      .then((values) => {
        if (!values) return;
        const organizationId = values.organization_id ?? eligible[0]?.id;
        if (!organizationId) return;
        const contactPhone = values.contact_phone.trim() || null;
        createMutation.mutate(
          {
            organization_id: organizationId,
            feed: values.feed,
            category_id: values.category_id,
            title: values.title.trim(),
            description: values.description.trim(),
            city: values.city.trim(),
            contact_phone: contactPhone,
            // №22: флаг живёт у телефона — без номера всегда false.
            contact_whatsapp: contactPhone !== null && Boolean(values.contact_whatsapp),
            contact_telegram: values.contact_telegram.trim() || null,
          },
          {
            onSuccess: () => {
              void message.success(t('listings.publish.success'));
              form.resetFields();
              onClose();
            },
          },
        );
      });
  };

  const handleClose = (): void => {
    createMutation.reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      title={t('listings.publish.modal_title')}
      okText={t('listings.publish.submit')}
      cancelText={t('common.cancel')}
      onOk={handleOk}
      onCancel={handleClose}
      confirmLoading={createMutation.isPending}
      okButtonProps={gateClosed ? { style: { display: 'none' } } : undefined}
      destroyOnClose
    >
      {gateClosed ? (
        <Alert
          type="warning"
          showIcon
          message={t('listings.publish.gate_title')}
          description={t(
            gateKind === 'no_contacts'
              ? 'listings.publish.contacts_gate_text'
              : 'listings.publish.gate_text',
          )}
        />
      ) : (
        <>
          {forbidden ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('listings.publish.gate_title')}
              description={t(
                forbiddenNoContacts
                  ? 'listings.publish.contacts_gate_text'
                  : 'listings.publish.error_forbidden',
              )}
            />
          ) : whatsappRequiresPhone ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('listings.publish.whatsapp_requires_phone')}
            />
          ) : (
            createMutation.error != null && (
              <div style={{ marginBottom: 16 }}>
                <ErrorAlert error={createMutation.error} />
              </div>
            )
          )}
          <Form<PublishValues>
            form={form}
            layout="vertical"
            // Город и контакты — из организации (№16); поля редактируемые.
            initialValues={{ feed: defaultFeed, ...defaultsFrom(eligible[0]) }}
            preserve={false}
          >
            {eligible.length > 1 && (
              <Form.Item
                name="organization_id"
                label={t('listings.publish.organization')}
                initialValue={eligible[0]?.id}
                rules={[{ required: true, message: t('listings.publish.required') }]}
              >
                <Select
                  options={eligible.map((o) => ({ value: o.id, label: o.name }))}
                  onChange={(id: string) =>
                    form.setFieldsValue(defaultsFrom(eligible.find((o) => o.id === id)))
                  }
                />
              </Form.Item>
            )}
            <Form.Item
              name="feed"
              label={t('listings.publish.feed')}
              rules={[{ required: true, message: t('listings.publish.required') }]}
            >
              <Select options={FEEDS.map((f) => ({ value: f, label: t(`listings.tabs.${f}`) }))} />
            </Form.Item>
            <Form.Item
              name="category_id"
              label={t('listings.publish.category')}
              rules={[{ required: true, message: t('listings.publish.required') }]}
            >
              <Select
                options={(categories ?? []).map((c) => ({
                  value: c.id,
                  label: categoryLabel(categories, c.id, i18n.language),
                }))}
              />
            </Form.Item>
            <Form.Item
              name="title"
              label={t('listings.publish.title')}
              rules={[
                { required: true, whitespace: true, message: t('listings.publish.required') },
              ]}
            >
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item
              name="description"
              label={t('listings.publish.description')}
              rules={[
                { required: true, whitespace: true, message: t('listings.publish.required') },
              ]}
            >
              <Input.TextArea rows={4} maxLength={4000} />
            </Form.Item>
            <Form.Item
              name="city"
              label={t('listings.publish.city')}
              rules={[
                { required: true, whitespace: true, message: t('listings.publish.required') },
              ]}
            >
              <Input maxLength={120} placeholder={t('listings.publish.city_placeholder')} />
            </Form.Item>
            <Form.Item
              name="contact_phone"
              label={t('listings.publish.phone')}
              extra={t('listings.publish.contacts_hint')}
              dependencies={['contact_telegram']}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_rule, value: string | undefined) =>
                    (value ?? '').trim() || (getFieldValue('contact_telegram') ?? '').trim()
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('listings.publish.contacts_required'))),
                }),
              ]}
            >
              <Input
                maxLength={32}
                placeholder="+7 700 000 00 00"
                // Очистили телефон → флаг WhatsApp снимается (как на бэкенде).
                onChange={(e) => {
                  if (!e.target.value.trim()) form.setFieldValue('contact_whatsapp', false);
                }}
              />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev: PublishValues, next: PublishValues) =>
                prev.contact_phone !== next.contact_phone
              }
            >
              {({ getFieldValue }) => {
                const hasPhone = Boolean((getFieldValue('contact_phone') ?? '').trim());
                return (
                  <Form.Item name="contact_whatsapp" valuePropName="checked">
                    <Checkbox disabled={!hasPhone}>{t('listings.publish.whatsapp')}</Checkbox>
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item name="contact_telegram" label={t('listings.publish.telegram')}>
              <Input maxLength={64} placeholder="@username" />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}
